import { getUser } from '@dealstack/frontend/shared/dataExchange/services/auth.service';
import { useTranslationAuth } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	fetchUserError,
	fetchUserSuccess,
} from '@dealstack/frontend/shared/mechanism/redux/reducers/user/user';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useEffect } from 'react';
import { focusManager, useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { UserAppDispatch } from '@dealstack/frontend/shared/other/react/types';
import { redirectToLoginPage } from '@dealstack/frontend/shared/redirecting';
import { ApplicationType } from '@dealstack/shared/lib/generated/models/ApplicationType';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';

import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { useAppSelector } from '@dealstack/frontend/admin/hooks/redux';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { DataUserResponse } from '@dealstack/shared/lib/generated';

export const USER_REFRESH_ON_FOCUS_AFTER_MS = 60_000;

export const useGetUserRequest = (dispatch: UserAppDispatch, redirectUrl?: string) => {
	const tAuth = useTranslationAuth();
	const applicationType = useAppSelector((state) => state.global.applicationType!);

	const q = useQuery({
		queryKey: [API_PATHS.USER_GET],
		queryFn: getUser,
		enabled: false,
		// Auth-check failure redirects to login below; no global error toast.
		meta: { suppressErrorToast: true },
	});

	useEffect(() => {
		if (!q.isSuccess) {
			return;
		}
		void (async () => {
			const user = q.data?.payload;
			if (!user) {
				return;
			}

			const hasNoAccess = user.platformAccess.length === 0;

			if (hasNoAccess) {
				const error = tAuth('permission.error');
				dispatch(fetchUserError(error));
				return;
			}

			let investor: DataUserResponse | undefined;

			if (applicationType === ApplicationType.INVESTOR) {
				try {
					investor = await makeRequest<ApiV1.Response<DataUserResponse>>({
						endpoint: formatUnicorn(API_PATHS.SHAREHOLDER_INVESTOR_USER_USERID_GET, {
							userId: user.id,
						}),
						method: HTTP_METHOD.GET,
					}).then((res) => res.payload);
				} catch (e) {
					// Sometimes we can have users that do not have an investor attached to them. In this case the API call fails with 404 and all code after is not being executed without any warning
					// eslint-disable-next-line no-console
					console.error(e);
				}
			}

			const userWithInvestor = { ...user, investor };
			dispatch(fetchUserSuccess(userWithInvestor));
			Sentry.setUser(user);
		})();
	}, [q.isSuccess, q.data]);

	useEffect(() => {
		if (!q.isError || q.data) {
			return;
		}
		redirectToLoginPage(redirectUrl);
	}, [q.isError]);

	const { isSuccess, dataUpdatedAt, refetch } = q;

	useEffect(
		() =>
			focusManager.subscribe((isFocused) => {
				const isStale = Date.now() - dataUpdatedAt > USER_REFRESH_ON_FOCUS_AFTER_MS;
				if (isFocused && isSuccess && isStale) {
					void refetch();
				}
			}),
		[isSuccess, dataUpdatedAt, refetch]
	);

	return q;
};
